import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    addAnchor = "addAnchor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.addAnchor]: boolean;
    };
    extendedStyles?: ActiveLinkListStyles;
}

export interface ActiveLinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
}

export const activeLinkListStyles: ActiveLinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
            .active span {
                border-bottom: ${getColor("primary")} 3px solid;
            }
        `,
    },
};

export const ActiveLinkList: FC<Props> = ({ fields, extendedStyles, location }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", activeLinkListStyles, extendedStyles);
    if (hasMissingCategories) {
        return null;
    }

    React.useEffect(() => {
        if (fields.addAnchor && location.pathname.includes("#")) {
            const node = document.getElementById("links-section");
            if (node) {
                setTimeout(() => {
                    node.scrollIntoView();
                }, 0);
            }
        }
    }, []);

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.linkListWrapper?.css}
    `;

    const directionStyles = css`
        ${fields.direction === "horizontal"
            ? `
                    display: inline-block;
                    padding: 1px 18px 0 0;

                    &:last-child {
                        padding: 1px 0 0 0;
                    }
                `
            : "padding: 1px 0 0 0;"};
        ${styles.linkWrapper?.css}
    `;

    const getPageUrl = (url: string) => {
        return `${url}${fields.addAnchor ? "#links-section" : ""}`;
    };

    const getLinkClass = (url: string) => {
        return getPageUrl(url) === location.pathname || url === location.pathname ? "active" : "";
    };

    return (
        <StyledWrapper css={alignmentStyles} id="links-section">
            <StyledWrapper {...styles.titleWrapper}>
                {titleLink ? (
                    <Link href={titleLink.url}>{fields.title || titleLink.title}</Link>
                ) : (
                    <Typography as="h3" {...styles.title}>
                        {fields.title}
                    </Typography>
                )}
            </StyledWrapper>
            {links.map((link, index) => (
                <StyledWrapper key={index} css={directionStyles}>
                    {link.url && (
                        <Link
                            href={getPageUrl(link.url)}
                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            className={getLinkClass(link.url)}
                        >
                            {fields.links[index].fields.overriddenTitle || link.title}
                        </Link>
                    )}
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ActiveLinkList),
    definition: {
        group: "Basic",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.addAnchor,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
